<template>
    <div class="h-dvh max-h-dvh flex flex-col overflow-hidden">
        <Notifications class="!top-[5.5rem] !right-4">
            <template #body="props">
                <div class="relative bg-white border border-gray-300 rounded-lg mb-4 p-4">
                    <h4 class="font-bold">
                        {{ props.item.title }}
                    </h4>
                    <button class="absolute top-2 right-2" @click="props.close">
                        <i class="fa fa-fw fa-close"></i>
                    </button>
                    <div v-html="props.item.text"></div>
                </div>
            </template>
        </Notifications>
        <NavBar
            v-model:centerOnMarker="centerOnMarker"
            :speedOptions="speedOptions"
            :daysPassed="daysPassed"
            :dayOfWeek="dayOfWeek"
            :saving="saving"
            @toggleSettings="toggleSettingsDialog"
            @togglePassport="togglePassportDialog"
            @toggleWallet="toggleWalletDialog"
            @toggleJobs="toggleJobsDialog"
            @saveGame="handleSaveGame"
            @resetGame="resetGame"
        />

        <div id="map" ref="mapContainer" class="flex-grow max-h-dvh"></div>

        <button
            class="fixed top-[4.5rem] md:top-[5.5rem] right-2 md:right-4 flex items-center justify-center w-12 h-12 bg-white rounded-full z-10 opacity-0 transition-opacity duration-300"
            :class="{'opacity-100': game}"
            @click="muted = !muted"
        >
            <img v-if="muted" :src="`${BaseUrl}img/icons/volume.svg`" alt="Volume" width="24" height="24" />
            <img v-if="!muted" :src="`${BaseUrl}img/icons/volume-x.svg`" alt="Volume mute" width="24" height="24" />
        </button>

        <!--- Settings -->
        <div class="fixed top-[5.5rem] left-4 flex flex-col gap-4 z-10">
            <GameSettings
                v-model="showSettingsDialog"
                :initialSettings="gameSettings"
                class="hidden md:block"
                @updateSettings="updateGameSettings"
                @close="showSettingsDialog = false"
            />

            <GameRules class="max-w-72" />

            <Button
                v-if="game"
                class="hidden md:block w-full py-2 text-red-600 font-bold bg-white rounded"
                @click="resetGame"
            >
                Give up
            </Button>
        </div>

        <!-- Confirm Route Button (only visible once a route has been planned) -->
        <ConfirmButtons
            v-if="route && !confirmed && !markerState.isMoving"
            :routePlan="{ routing, mode, travelTime, distance, cost, currency_symbol }"
            @confirm="confirmRoute(gameTime, moveMarkerAlongRoute);"
            @cancel="cancelRoute"
        />

        <!-- Off-screen marker indicator -->
        <div 
            v-if="game && markerOffScreen.isOffScreen" 
            class="fixed z-50 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-lg cursor-pointer transition-all duration-300 hover:bg-blue-600 will-change-transform"
            :style="{
                left: markerOffScreen.indicatorX + 'px',
                top: markerOffScreen.indicatorY + 'px',
                transform: `translate3d(-50%, -50%, 0) rotate(${markerOffScreen.angle}deg)`
            }"
            @click="centerOnPlayer"
        >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
            </svg>
        </div>

        <!-- Stations Filtered Message -->
        <div v-if="game && !markerState.isMoving && (showStationsFiltered || markersHiddenDueToZoom || isLoading)" class="loading-indicator fixed top-28 md:top-32 left-1/2 -translate-x-1/2 bg-white w-max px-4 py-2 rounded shadow-lg z-50 flex items-center gap-2">
            <span v-if="isLoading">Loading...</span>
            <span v-else-if="markersHiddenDueToZoom">Zoom in to view stations and points of interest</span>
            <span v-else-if="showStationsFiltered">Some stations hidden, zoom in</span>
        </div>

        <!-- Game Paused Indicator -->
        <div v-if="game && isGamePaused" class="fixed top-20 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white px-4 py-2 rounded shadow-lg z-50">
            <div class="flex items-center gap-2">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                </svg>
                <span>Game Paused - Tab Inactive</span>
            </div>
        </div>

        <!-- Traveling Info & “Get Off” Button -->
        <div v-if="markerState.isMoving && isNextStopAvailable" class="fixed bottom-28 md:bottom-14 left-1/2 w-max p-3 bg-white rounded-lg -translate-x-1/2 z-50">
          <p class="font-semibold">Heading to: {{ finalDestinationName }}</p>
          <p>Next stop: {{ nextStopName }}</p>
          <button
                v-if="markerState.getOffAtStopIndex === null"
                class="mt-2 px-4 py-2 bg-red-500 text-white rounded"
                @click="getOffNextStop"
            >
                Get off at next stop
            </button>
        </div>

        <WelcomeDialog
            v-if="showWelcome"
            @logged-in="handleUserLogin"
        />

        <RegisterDialog
            v-if="showRegister"
            :user="user"
            @registered="saveGame"
        />

        <GameMenu
            v-if="showGameMenu"
            v-model="showGameMenu"
            :games="availableRaces"
            @select-game="handleGameSelect"
            @start-game="handleGameStart"
        />

        <!--<PassportNew
            v-model="showPassport"
            :countries="countriesVisited"
            class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        />-->

        <Wallet
            v-model="showWallet"
            :money="money"
            class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        />

        <TrainTimeTable
            v-model="showTrainTimeTable"
            :station="selectedStation"
            :money="money"
            :time="gameTime + timeZoneOffset"
            :dayOfWeek="dayOfWeekInt"
            class="fixed top-0 left-0"
            @bought-ticket="(data) => scheduleDeparture(data, 'train')"
            @close="showTrainTimeTable = false"
        />

        <BusTimeTable
            v-model="showBusTimeTable"
            :station="selectedStation"
            :money="money"
            :time="gameTime + timeZoneOffset"
            :dayOfWeek="dayOfWeekInt"
            class="fixed top-0 left-0"
            @bought-ticket="(data) => scheduleDeparture(data, 'bus')"
            @close="showBusTimeTable = false"
        />

        <BoatTimeTable
            v-model="showBoatTimeTable"
            :station="selectedStation"
            :money="money"
            :time="gameTime + timeZoneOffset"
            :dayOfWeek="dayOfWeekInt"
            class="fixed top-0 left-0"
            @bought-ticket="(data) => scheduleDeparture(data, 'boat')"
            @close="showBoatTimeTable = false"
        />

        <MultiTimeTable
            v-model="showMultiTimeTable"
            :station="selectedStation"
            :money="money"
            :time="gameTime + timeZoneOffset"
            :dayOfWeek="dayOfWeekInt"
            class="fixed top-0 left-0"
            @select-train="() => { showMultiTimeTable = false; showTrainTimeTable = true; }"
            @select-bus="() => { showMultiTimeTable = false; showBusTimeTable = true; }"
            @close="showMultiTimeTable = false"
        />

        <JobsBoard
            v-model="showJobsBoard"
            :jobs="jobs"
            :money="money"
            :time="gameTime + timeZoneOffset"
            class="fixed top-0 right-0"
            @close="showJobsBoard = false"
        />

        <JobMenu
            v-model="showJobMenu"
            :job="selectedJob"
            :currentTime="gameTime + timeZoneOffset"
            @close="showJobMenu = false"
            @start-job="handleStartJob"
        />

        <div v-if="showJobInProgress" class="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white p-4 rounded shadow-lg z-50">
            <p>You are currently working at: <span class="font-semibold">{{ selectedJob.name }}</span></p>
        </div>

        <HotelMenu
            v-model="showHotelMenu"
            :hotel="selectedHotel"
            @stay="handleHotelStay"
            @close="showHotelMenu = false"
        />

        <BankMenu
            v-model="showBankMenu"
            :bank="selectedBank"
            :money="money"
            @exchanged="handleExchange"
            @close="showBankMenu = false"
            class="fixed top-0 right-0"
        />

        <div v-if="showAutoSleepDialog" class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div class="bg-white p-5 border-2 border-gray-500 rounded-lg w-7/12 max-w-2xl max-h-[90%] overflow-y-auto font-mono relative">
                <h2 class="font-bold text-lg">Auto Sleep</h2>
                <p class="mb-4">
                    Because you failed to find a hotel for the night you have gone into auto sleep
                    and been charged <strong>{{ autoSleepPenalty.price }} {{ autoSleepPenalty.currency_code }}</strong> as a penalty.
                </p>
                <p class="mb-4">The penalty is calculated based on the most expensive hotel in the area +10%.</p>
                <div class="flex justify-end space-x-2">
                    <button class="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50" @click="dismissAutoSleep">Continue</button>
                </div>
            </div>
        </div>

        <div v-if="showGameOverDialog" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
            <div class="bg-white rounded-lg shadow-xl m-4 md:m-0 p-6 max-w-xl w-full text-center">
                <h2 class="text-2xl font-bold mb-4">Game Over</h2>
                <p class="mb-4">You have run out of money and can't afford a hotel for tonight.</p>
                <p class="mb-4">Sadly my friend, you can no longer continue your journey.</p>
                <button
                    class="mt-6 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition"
                    @click="resetGame"
                >
                    Start New Game
                </button>
            </div>
        </div>

        <div 
            v-if="hoveredMarker" 
            class="fixed bg-white border border-gray-300 rounded-lg p-2 pointer-events-auto z-[1001] whitespace-nowrap"
            :style="{
                left: tooltipPosition.x + 'px',
                top: tooltipPosition.y + 'px',
                transform: 'translate(-0.5rem, calc(-100% - 0.25rem))'
            }"
            @mouseenter="isHoveringTooltip = true"
            @mouseleave="handleTooltipLeave"
        >
            <img v-if="hoveredMarker.entity === 'finish'" :src="`${BaseUrl}img/finish.png`" width="22" height="22" />
            <template v-else-if="hoveredMarker.entity === 'station'">
                <img v-if="hoveredMarker.type == '1'" :src="`${BaseUrl}img/icons/train.svg`" width="22" height="22" />
                <img v-if="hoveredMarker.type == '2'" :src="`${BaseUrl}img/icons/bus.svg`" width="22" height="22" />
                <img v-if="hoveredMarker.type == '5'" :src="`${BaseUrl}img/icons/ferry.svg`" width="22" height="22" />
                <img v-if="hoveredMarker.type == '9'" :src="`${BaseUrl}img/icons/multi.svg`" width="22" height="22" />
            </template>
            <img v-else :src="`${BaseUrl}img/icons/${hoveredMarker.entity}.svg`" width="22" height="22" />
            <span class="block">{{ hoveredMarker.name }}</span>
            <template v-if="hoveredMarker.entity === 'job'">
                <span class="block">{{ hoveredMarker.start }} - {{ hoveredMarker.end }}</span>
                <span class="block">{{ hoveredMarker?.money }} {{ hoveredMarker?.currency }}</span>
            </template>
            <button 
                class="px-2 py-1 bg-green-500 text-white rounded mr-2"
                @click="planRoute('walk', hoveredMarker)"
            >
                Walk here
            </button>
            <button 
                class="px-2 py-1 bg-blue-500 text-white rounded"
                @click="planRoute('taxi', hoveredMarker)"
            >
                Taxi here
            </button>
        </div>

        <div v-if="routing" class="fixed bottom-32 md:bottom-14 left-1/2 flex items-center gap-x-2 w-max px-6 py-3 bg-white rounded-lg -translate-x-1/2">
            <LoadingIcon class="w-8 h-8" />
            <span>Finding route...</span>
        </div>

        <div
            v-if="proximityMessage"
            class="fixed bottom-[4.375rem] md:bottom-0 left-0 flex justify-center items-center gap-x-4 w-screen bg-white font-bold text-center px-2 pt-2 md:py-4 z-[2000]"
            :class="proximityMessageClass"
        >
            {{ proximityMessage }}
            <Button v-if="proximityStation && !markerState.isMoving" class="shrink-0 bg-blue-600 text-white px-4 py-2 rounded shadow-lg" @click="gameTime = Math.max(gameTime, proximityStation.triggerTime)">Skip to {{ proximityStation?.depart }}</Button>
        </div>

        <CountryDialog
            v-if="countryNotification"
            :country-code="currentCountryCode"
            :timezone-offset="lastTimeZoneOffset"
            @close="countryNotification = false"
        />

        <FinishDialog
            v-model="hasFinished"
            :saved="finishSaved"
            :finish-time="finishTime"
            :game-id="game?.id"
            :race-id="race?.id"
            @share-result="shareRaceResult"
        />
    </div>
</template>
  
<script setup>
import { ref, onMounted, reactive, computed, onUnmounted, shallowRef, watch, markRaw } from "vue";
import { storeToRefs } from 'pinia';
import { INTERACTION_DISTANCE, isPublicTransport, roundMoney, validRoute, departureTime, routeTiming, routePosition, shouldShowStation as stationVisible } from '@/utils/gameplay.js';
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import tzLookup from 'tz-lookup';
import { playNationalAnthem } from '@/utils/sound.js';
import { diffZonesInMinutes, getCountryCodeForTimezone } from '@/utils/time.js';
import { notify } from "@kyvg/vue3-notification";

import LoadingIcon from '@/assets/icons/Loading.vue';
import NavBar from '@/components/NavBar.vue';
import BankMenu from '@/components/BankMenu.vue';
import Button from '@/components/Button.vue';
import ConfirmButtons from '@/components/ConfirmButtons.vue';
import CountryDialog from '@/components/CountryDialog.vue';
import FinishDialog from '@/components/FinishDialog.vue';
import PassportNew from '@/components/PassportNew.vue';
import Wallet from "@/components/Wallet.vue";
import TrainTimeTable from "@/components/TrainTimeTable.vue";
import BusTimeTable from "@/components/BusTimeTable.vue";
import BoatTimeTable from "@/components/BoatTimeTable.vue";
import MultiTimeTable from "@/components/MultiTimeTable.vue";
import JobsBoard from "@/components/JobsBoard.vue";
import JobMenu from "@/components/JobMenu.vue";
import WelcomeDialog from "@/components/WelcomeDialog.vue";
import RegisterDialog from "@/components/RegisterDialog.vue";
import HotelMenu from '@/components/HotelMenu.vue';
import GameRules from "@/components/GameRules.vue";
import GameSettings from '@/components/GameSettings.vue';
import GameMenu from '@/components/GameMenu.vue';
import { useMapbox } from '@/composables/useMapbox';
import { useRouting } from '@/composables/useRouting';
import { useGameStore } from '@/stores/useGameStore.js'
import { timeStringToSeconds, secondsToTimeString, getNextAbsoluteTriggerTime } from '@/utils/time.js';
import { capitalizeWords } from "./utils/string";

const BaseUrl = ref(baseUrl);

const saving = ref(false);
const muted = ref(false);

const countryNotification = ref(false);
  
// ───── Mapbox Setup ─────────────────────────────────────────────
// Token resolution: prefer Vite env var VITE_MAPBOX_TOKEN (set via .local.env or .prod.env),
// fall back to the previous hardcoded production token to avoid breaking local dev.
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || "pk.eyJ1IjoicGF0dGxlIiwiYSI6ImNtaTM5dGJiZTBnMW8ya3F2ZDJma2FwNTcifQ.MjNHplZhL2bi7jTIMlMnGg";

// ───── References and Global Variables ──────────────────────────
const mapContainer = ref(null);
let map = null; // Will be set on mount.
const mapRef          = shallowRef(null);
const personMarkerRef = shallowRef(null);
const centerOnMarker = ref(true); // Whether to center the map on the person marker.
let destinationMarker = null; // The marker placed when the user clicks on the map.

const scheduledEvents = [];

// initialize from server-injected globals:
const gameStore = useGameStore()
gameStore.setUser(_user)
gameStore.setGame(_game)
gameStore.setRace(_race)
gameStore.setMoney(_money)
gameStore.setGameTime(_game_time)
const initialTimeZone = _game ? tzLookup(Number(player_coordinates[1]), Number(player_coordinates[0])) : _race.start_timezone;
gameStore.setTimeZone(initialTimeZone)
gameStore.setTimeZoneOffset(diffZonesInMinutes(_race.start_timezone, initialTimeZone) * 60)
gameStore.setCurrentCountryCode(_game ? getCountryCodeForTimezone(initialTimeZone) : _race.start_country)
gameStore.setGameSpeed(1) // multiplier for game speed
gameStore.setLastSavedGameTime(_game_time) // last time we saved the game

const { addMoney, deductMoney, tickTime, logGame, saveGame } = gameStore;
const {
    user, game, race, money,
    gameTime, timeZone, timeZoneOffset, lastTimeZoneOffset,
    currentCountryCode, gameSpeed, lastSavedGameTime,
    showSettingsDialog, gameSettings,
} = storeToRefs(gameStore)

const speedOptions = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000];

const daysPassed = computed(() => Math.floor((gameTime.value + timeZoneOffset.value) / 86400));
const dayOfWeek = computed(() => {
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    return days[daysPassed.value % 7];
});
const dayOfWeekInt = computed(() => {
    return (daysPassed.value % 7) + 1;
});

// Computed property to determine if it's nighttime (9pm-6am)
const isNighttime = computed(() => {
    const secondsInDay = gameTime.value % (24 * 3600); // Get seconds in current day
    const hours = Math.floor(secondsInDay / 3600); // Convert to hours
    return hours >= 21 || hours < 6; // 9pm (21:00) to 6am
});

// Map styles for day and night
const dayStyle = 'mapbox://styles/mapbox/streets-v11';
const nightStyle = 'mapbox://styles/mapbox/dark-v11';

const availableRaces = ref(available_races);

let startMarker = null;
let finishMarker = null;

const showWelcome = ref(!user.value.id);
const showRegister = ref(false);
const showGameMenu = ref(!_game && user.value.id);
const showPassport = ref(false);
const showWallet = ref(false);
const showTrainTimeTable = ref(false);
const showBusTimeTable = ref(false);
const showBoatTimeTable = ref(false);
const showMultiTimeTable = ref(false);
const showJobsBoard = ref(false);
const showJobMenu = ref(false);
const showJobInProgress = ref(false);
const showHotelMenu = ref(false);
const showBankMenu   = ref(false);
const showAutoSleepDialog = ref(false);
const showGameOverDialog = ref(false);

const isGamePaused = ref(false); // Track if game is paused due to tab being inactive

const autoSleepPenalty = ref({});
let skipAutoSleep = _skip_auto_sleep && ((gameTime.value + timeZoneOffset.value) % 86400 >= 7200)
    && ((gameTime.value + timeZoneOffset.value) % 86400 < 10800);
const selectedStation = ref();
const destinationNotification = ref(false);

const scheduledStations = ref([]); // Array of {lng, lat, stationId}
const proximityMessage = ref(""); // Message for the user
const proximityMessageClass = computed(() => {
    return proximityMessage.value.includes('You are close enough') ? 'text-green-600' : 'text-red-600';
});
const proximityStation = ref();

const hasFinished = ref(false);
const finishSaved = ref(false);
const finishTime = ref('');

const countriesVisited = ref([]);


  // ───── Marker Movement & Game Loop (existing code) ───────────────
const markerState = reactive({
    isMoving: false,
    marker: null,
    startCoords: null,
    endCoords: null,
    startTime: null,
    durationInGameSeconds: null,
    map: null,
    route: null,         // Array of coordinates for the confirmed route
    segmentTimes: null,  // Precomputed durations per segment (in game seconds)

    getOffAtStopIndex: null,   // which stop to disembark at
    hasGotOff: false,          // whether we already got off
    stops: null,             // ← will hold the full stops array
    currentStopIndex: -1,    // ← index of the last‐departed stop
    stopArrivalTimes: null,  // ← cumulative times (in game seconds) for each stop
});
  
// Game loop variables
let lastUpdateTime = performance.now();
let animationFrameId = null;
const realToGameTimeRatio = 3; // 1 real sec = 3 game minutes at 1x speed
let lastWakeUpCall = 0; // Track the last day we played the wake-up sound

const { getMap, isReady, setMapStyle } = useMapbox(mapContainer, {
    accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
    center: player_coordinates,
    zoom: 14,
    onLoad: handleMapLoad,
    onMove: handleMapMove,
    onMoveEnd: handleMapMoveEnd,
    onClick: handleMapClick,
    onZoom: handleMapZoom,
});

const {
    route,
    mode,
    travelTime,
    distance,
    cost,
    currency_symbol,
    destination,
    confirmed,
    routing,
    addDestinationMarker,
    addConfirmedRouteLayers,
    planRoute,
    confirmRoute,
    cancelRoute
} = useRouting(mapRef, personMarkerRef, logGame, deductMoney, moveMarkerAlongRoute, () => !markerState.isMoving && !hasFinished.value);

watch(isReady, ready => {
    if (!ready) return;

    map = getMap();
    mapRef.value = markRaw( getMap() );

    showStationsFiltered.value = map.getZoom() < 12;
  
    // Create the person marker (the one that will later be animated along a route).
    const markerElement = document.createElement("div");
    markerElement.className = "player-marker";
    personMarkerRef.value = new mapboxgl.Marker(markerElement)
        .setLngLat(game.value ? player_coordinates : race.value.startCoords)
        .addTo(map);

    addStartAndFinishMarkers(race.value.startCoords, race.value.endCoords);
  
    // Set up nighttime watcher now that map is ready
    /*watch(isNighttime, (isNight) => {
        const newStyle = isNight ? nightStyle : dayStyle;
        setMapStyle(newStyle);
    }, { immediate: true });*/

    if(game.value?.id) {
        startGameLoop();
    }

    fetchStations();
    fetchJobs();
    fetchHotels();
    fetchBanks();
});

function handleUserLogin(loggedInUser) {
    gameStore.setUser(loggedInUser);
    showWelcome.value = false;
    // Instead of starting the game loop immediately,
    // show the game menu so the user can select a scenario.
    showGameMenu.value = true;
}

function handleGameSelect(selectedGame) {
    stopGameLoop();
    console.log("Selected game:", selectedGame);
    gameStore.setRace(selectedGame)
    gameStore.setMoney(selectedGame.money)
    gameStore.setGameTime(selectedGame.start_time)
    gameStore.setTimeZone(selectedGame.start_timezone)
    gameStore.setTimeZoneOffset(0) // will be set based on the user's location
    gameStore.setCurrentCountryCode(selectedGame.start_country) // default country code
    gameStore.setGameSpeed(1) // multiplier for game speed
    gameStore.setLastSavedGameTime(selectedGame.start_time) // last time we saved the game

    // Set the map center to the starting coordinates of the selected game without animation.
    map?.setCenter(selectedGame.startCoords);

    // Also set the person marker to the starting coordinates.
    personMarkerRef.value?.setLngLat(selectedGame.startCoords);

    if (map) addStartAndFinishMarkers(selectedGame.startCoords, selectedGame.endCoords);
}

async function handleGameStart(selectedRace) {
    handleGameSelect(selectedRace);
    scheduledEvents.length = 0;
    scheduledStations.value = [];
    hasFinished.value = false;
    finishSaved.value = false;
    lastDaySlept = Math.floor(Number(selectedRace.start_time) / 86400);
    skipAutoSleep = false;
    try {
        await saveGame(selectedRace.startCoords[1], selectedRace.startCoords[0]);
        if (personMarkerRef.value) startGameLoop();
    } catch (error) {
        showGameMenu.value = true;
        notify({ title: 'Could not start race', text: 'Please try again.', type: 'error' });
    }
}

function handleSaveGame() {
    if(user.value.email_address) {
        const personMarkerLngLat = personMarkerRef.value.getLngLat();
        saveGame(personMarkerLngLat.lat, personMarkerLngLat.lng);
    } else {
        showRegister.value = true;
    }
}

function resetGame() {
    gameStore.resetGame({}, () => {
        window.location.reload();
    });
}

function addStartAndFinishMarkers(startCoords, endCoords) {
    if (startMarker) startMarker.remove();
    if (finishMarker) finishMarker.remove();

    const startEl = document.createElement('img');
    startEl.src = `${baseUrl}img/start.png`;
    startEl.style.width = '48px';
    startEl.style.height= '48px';
    startEl.style.cursor= 'pointer';
    startMarker = new mapboxgl.Marker({ element: startEl, anchor: 'bottom' }).setLngLat(startCoords).addTo(map);

    const finishEl = document.createElement('img');
    finishEl.src = `${baseUrl}img/finish.png`;
    finishEl.style.width = '48px';
    finishEl.style.height = '48px';
    finishEl.style.cursor = 'pointer';
    
    // Add hover functionality to finish marker
    finishEl.addEventListener("mouseenter", (e) => {
        e.stopPropagation();
        const rect = finishEl.getBoundingClientRect();
        tooltipPosition.value = {
            x: rect.left,
            y: rect.top
        };
        hoveredMarker.value = {
            name: 'Finish Line',
            lat: endCoords[1],
            lng: endCoords[0],
            entity: 'finish'
        };
        isHoveringMarker.value = true;
    });

    finishEl.addEventListener("mouseleave", (e) => {
        e.stopPropagation();
        isHoveringMarker.value = false;
        setTimeout(() => {
            if (!isHoveringTooltip.value && !isHoveringMarker.value) {
                hoveredMarker.value = null;
            }
        }, 50);
    });
    
    finishMarker = new mapboxgl.Marker({ element: finishEl, anchor: 'bottom' }).setLngLat(endCoords).addTo(map);
}

function toggleSettingsDialog() {
    showSettingsDialog.value = !showSettingsDialog.value;
    showPassport.value = false;
    showJobsBoard.value = false;
    showWallet.value = false;
}

function togglePassportDialog() {
    showPassport.value = !showPassport.value;
    showSettingsDialog.value = false;
    showJobsBoard.value = false;
    showWallet.value = false;
}

function toggleWalletDialog() {
    showWallet.value = !showWallet.value;
    showSettingsDialog.value = false;
    showPassport.value = false;
    showJobsBoard.value = false;
}

function toggleJobsDialog() {
    showJobsBoard.value = !showJobsBoard.value;
    showSettingsDialog.value = false;
    showPassport.value = false;
    showWallet.value = false;
}

function updateRouteProgress(currentCoord, currentSegment, progress) {
  const routeCoords = markerState.route; // Array of coordinates
  let completedCoords = [];
  let remainingCoords = [];
  
  
  // If we are still in the first segment:
  if (currentSegment === 0) {
    completedCoords.push(routeCoords[0], currentCoord);
    remainingCoords = [currentCoord, ...routeCoords.slice(1)];
  } else {
    // Completed portion: from start up to current segment plus the interpolated currentCoord.
    completedCoords = routeCoords.slice(0, currentSegment + 1);
    completedCoords.push(currentCoord);
    // Remaining portion: from currentCoord to the end.
    remainingCoords.push(currentCoord, ...routeCoords.slice(currentSegment + 1));
  }
  
  // Update the sources if they exist.
  const completedSource = map.getSource("route-completed");
  if (completedSource) {
    completedSource.setData({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: completedCoords,
      },
    });
  }
  const remainingSource = map.getSource("route-remaining");
  if (remainingSource) {
    remainingSource.setData({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: remainingCoords,
      },
    });
  }
}

function handleRouteCompletion() {
    // Remove the route overlay.
    removeLayerAndSource('route-completed');
    removeLayerAndSource('route-remaining');

    const el = personMarkerRef.value.getElement();
    el.classList.remove("transport-marker");
    el.classList.add("player-marker");

    // Play a chime sound (ensure chime.mp3 is in your public folder).
    const audio = new Audio("sounds/chimes.mp3");
    audio.volume = muted.value ? 0 : 0.5;
    audio.play().catch(() => {});

    centerOnMarker.value = false;
    gameSpeed.value = 1;

    skipAutoSleep = false;

    const markerLngLat = personMarkerRef.value.getLngLat();
    if (map.getBounds().contains(markerLngLat)) {
        // Center on the marker and zoom to level 16.
        map.flyTo({ center: markerLngLat, zoom: 16});
    } else {
        destinationNotification.value = true;
    }

    updatePersonMarkerForTransport()
}

const finalDestinationName = computed(() => {
    const stops = markerState.stops || [];
    return stops[stops.length-1]?.station_name || '';
});

const nextStopName = computed(() => {
    const idx = markerState.currentStopIndex + 1;
    return markerState.stops?.[idx]?.station_name || '';
});

const isNextStopAvailable = computed(() => {
    const idx = markerState.currentStopIndex + 1;
    return markerState.stops && idx < markerState.stops.length;
});

const nextStopCoords = computed(() => {
    const idx = markerState.currentStopIndex + 1;
    const s = markerState.stops?.[idx];
    return s ? [ parseFloat(s.lng), parseFloat(s.lat) ] : null;
});

function getOffNextStop() {
    const idx = markerState.currentStopIndex + 1;
    if (idx < 0 || idx >= markerState.stops.length) return;
    if (!window.confirm(`Leave this ride at ${nextStopName.value}? Your remaining ticket will be lost.`)) return;

    // schedule disembark at that stop
    markerState.getOffAtStopIndex = idx;
}

  
const gameLoop = (timestamp) => {
    animationFrameId = null;
    if (!gameLoopRunning) return;
    // Skip game time updates if paused (tab inactive)
    if (isGamePaused.value) {
        animationFrameId = requestAnimationFrame(gameLoop);
        return;
    }
    
    const delta = (timestamp - lastUpdateTime) / 1000;
    lastUpdateTime = timestamp;
    gameTime.value += delta * realToGameTimeRatio * gameSpeed.value;
    const wasOnPublicTransport = markerState.isMoving && isPublicTransport(markerState.travelMode);

    // Every hour in game time call logGame to record the time.
    if (gameTime.value - lastSavedGameTime.value >= 3600) {
        logGame({
            event: 'Ping',
            from_lat: personMarkerRef.value.getLngLat().lat,
            from_lng: personMarkerRef.value.getLngLat().lng,
            to_lat: personMarkerRef.value.getLngLat().lat,
            to_lng: personMarkerRef.value.getLngLat().lng,
            start: gameTime.value,
            end: gameTime.value,
        });
        lastSavedGameTime.value = gameTime.value;
    }

    // Play cock-a-doodle doo sound at 6am (21600 seconds = 6 hours)
    const gameDay = Math.floor(gameTime.value / 86400);
    const secondsIntoDay = gameTime.value % 86400;
    
    if (secondsIntoDay >= 21600 && gameDay > lastWakeUpCall) {
        const audio = new Audio("sounds/cock-a-doodle-doo.mp3");
        audio.volume = muted.value ? 0 : 0.5;
        audio.play().catch(() => {});
        lastWakeUpCall = gameDay;
    }

    if (markerState.stops) {
        const nextIdx = markerState.stops.findIndex(
            stop => gameTime.value < markerState.startTime + (stop.duration_seconds || 0)
        );

        // ② if it changed, update currentStopIndex to “last-passed”
        //    (nextIdx===0 means we haven’t passed any yet → currentStopIndex stays -1)
        markerState.currentStopIndex = nextIdx === -1 ? markerState.stops.length - 1 : nextIdx - 1;
    }


    // 2. Disembark check: once we hit the selected stopArrivalTime, stop moving
    if ( markerState.getOffAtStopIndex !== null && !markerState.hasGotOff && gameTime.value >= markerState.stopArrivalTimes[markerState.getOffAtStopIndex]) {
        // stop movement
        markerState.isMoving = false;
        markerState.hasGotOff = true;
        const stop = markerState.stops[markerState.getOffAtStopIndex];
        markerState.marker.setLngLat([Number(stop.lng), Number(stop.lat)]);

        handleRouteCompletion();
    }

    // Update station circles each frame (or adjust frequency as needed)
    updateAllMarkers(false);

    // Animate person marker along a confirmed route (if one is active).
    if (markerState.isMoving && markerState.route) {
        const position = routePosition(markerState.route, markerState.segmentTimes, gameTime.value - markerState.startTime);
        const { segment: currentSegment, progress, coordinate: [currentLng, currentLat] } = position;
        // Update marker position.
        markerState.marker.setLngLat([currentLng, currentLat]);

        if (centerOnMarker.value) {
            suppressMapEvents();
            markerState.map.setCenter([currentLng, currentLat]);
        }

        // Update the confirmed route progress (if the layers have been created).
        if (map.getSource("route-completed") && map.getSource("route-remaining")) {
            updateRouteProgress([currentLng, currentLat], currentSegment, progress);
        }

        // Stop moving if route is complete.
        if (currentSegment === markerState.segmentTimes.length - 1 && progress >= 1) {
            if (!markerState.routeCompleted) {
                markerState.routeCompleted = true;
                markerState.isMoving = false;
                handleRouteCompletion();
            }
        }
    }

    // Process in chronological order, including when a fast frame crosses several stops.
    scheduledEvents.sort((a, b) => a.triggerTime - b.triggerTime);
    while (scheduledEvents.length && gameTime.value >= scheduledEvents[0].triggerTime) {
        const event = scheduledEvents.shift();
        event.callback();
        scheduledEvents.sort((a, b) => a.triggerTime - b.triggerTime);
    }

    const currentDay = Math.floor((gameTime.value + timeZoneOffset.value) / 86400);
    if (currentDay > lastDaySlept) {
        const secondsToday = (gameTime.value + timeZoneOffset.value) % 86400;
        if (secondsToday >= 7200 && secondsToday < 10800) {
            if (!skipAutoSleep && !wasOnPublicTransport && !(markerState.isMoving && isPublicTransport(markerState.travelMode))) {
                if(cache.hotels.size > 0) {
                    lastDaySlept = currentDay;
                    console.log(Array.from(cache.hotels.values()));
                    // Find the most expensive hotel from those loaded.
                    const mostExpensive = Array.from(cache.hotels.values()).reduce((max, current) => {
                        return parseFloat(current.price) > parseFloat(max.price) ? current : max;
                    });
                    console.log(mostExpensive)
                    // Add a 10% penalty to the most expensive hotel.
                    const price = (parseFloat(mostExpensive.price) * 1.1).toFixed(2);

                    const result = deductMoney(price, mostExpensive.currency_code);
                    if(!result) {
                        showGameOverDialog.value = true;
                        stopGameLoop();
                        return;
                    }

                    autoSleepPenalty.value = { ...mostExpensive, price };

                    const startTime = gameTime.value;

                    // Fast-forward to 7am next day.
                    fastForwardToTime(secondsToTimeString(timeStringToSeconds("07:00") - timeZoneOffset.value));

                    logGame({
                        event: 'Sleep',
                        from_lat: personMarkerRef.value.getLngLat().lat,
                        from_lng: personMarkerRef.value.getLngLat().lng,
                        to_lat: personMarkerRef.value.getLngLat().lat,
                        to_lng: personMarkerRef.value.getLngLat().lng,
                        start: startTime,
                        end: gameTime.value,
                        money: price,
                        currency: mostExpensive.currency_code,
                    });

                    showAutoSleepDialog.value = true;
                    stopGameLoop();
                    return;
                }
            } else {
                lastDaySlept = currentDay;
                skipAutoSleep = false;
            }
        }
    }

    updateProximityMessage();

    // Check distance to finish marker
    checkFinishMarkerProximity();

    // Update off-screen marker indicator
    updateOffScreenIndicator();

    if (gameLoopRunning) animationFrameId = requestAnimationFrame(gameLoop);
};

let gameLoopRunning = false;
const startGameLoop = () => {
    if (gameLoopRunning) return;
    gameLoopRunning = true;
    lastUpdateTime = performance.now();
    animationFrameId = requestAnimationFrame(gameLoop);
};
  
const stopGameLoop = () => {
    gameLoopRunning = false;
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
    }
    animationFrameId = null;
};
  
// Moves the given marker along a GeoJSON route over a given in-game duration (in hours)
function moveMarkerAlongRoute(marker, routeGeoJSON, durationInGameSeconds, stops, map, travelMode = null) {
    const { coordinates, segmentTimes } = routeTiming(routeGeoJSON, durationInGameSeconds, stops);
    if(travelMode !== 'walk') {
        const audio = new Audio(`sounds/${travelMode}.mp3`);
        audio.volume = muted.value ? 0 : 0.5; // Set volume to 50% or mute
        audio.play();
    }

    gameSpeed.value = 100;

    markerState.travelMode = travelMode;
    markerState.isMoving = true;
    markerState.routeCompleted = false;
    markerState.marker = marker;
    markerState.route = coordinates;
    markerState.segmentTimes = segmentTimes;
    markerState.startTime = gameTime.value;
    markerState.map = map;
    markerState.stops = stops;
    markerState.currentStopIndex = -1;
    // ○ build absolute arrival times (game seconds from startTime)
    markerState.stopArrivalTimes = stops.map(s => 
      // stops[i].duration_seconds is already time-from-departure
      markerState.startTime + (s.duration_seconds || 0)
    );

    markerState.getOffAtStopIndex = null;
    markerState.hasGotOff        = false;
}
  
function calculateDistance(coord1, coord2) {
    const R = 6371e3;
    const toRadians = (deg) => (deg * Math.PI) / 180;
    const lat1 = toRadians(coord1[1]);
    const lat2 = toRadians(coord2[1]);
    const deltaLat = toRadians(coord2[1] - coord1[1]);
    const deltaLng = toRadians(coord2[0] - coord1[0]);
    const a =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}
  
function calculateSegmentTimes(coordinates, totalDistance, durationInGameSeconds) {
    const segmentTimes = [];
    for (let i = 0; i < coordinates.length - 1; i++) {
        const segmentDistance = calculateDistance(coordinates[i], coordinates[i + 1]);
        segmentTimes.push((segmentDistance / totalDistance) * durationInGameSeconds);
    }
    return segmentTimes;
}
  
function calculateTotalDistance(coordinates) {
    let totalDistance = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
        totalDistance += calculateDistance(coordinates[i], coordinates[i + 1]);
    }
    return totalDistance;
}
  
// ───── Station Fetching & Marker Management ──────────────────────────────────────

// We need access to the map object in these functions so we declare it here.

// Prevent overlapping API calls.
const isFetching = reactive({
    stations: false,
    jobs: false,
    hotels: false,
    banks: false
});

// Use Maps to cache objects by their id
const cache = {
    stations: new Map(),
    jobs: new Map(),
    hotels: new Map(),
    banks: new Map()
};

// And Maps to keep track of marker objects by id
const markers = {
    stations: new Map(),
    jobs: new Map(),
    hotels: new Map(),
    banks: new Map()
};

// Keep track of the last "expanded" bounds (fetched from the API)
let lastFetchedBounds = null;
let lastFetchedZoom;

//const jobs = ref([]);
const selectedJob = ref(null);

const selectedHotel = ref(null);
let lastDaySlept = 0;

const selectedBank = ref(null);
  
// ───── Map Event Suppression (unchanged) ──────────────────────────
let suppressEvents = false;
const suppressMapEvents = (suppressDuration = 300) => {
    suppressEvents = true;
    setTimeout(() => {
        suppressEvents = false;
    }, suppressDuration);
};

function removeLayerAndSource(id) {
    if (map.getLayer(id)) {
        map.removeLayer(id);
    }
    if (map.getSource(id)) {
        map.removeSource(id);
    }
}

  
// ───── Map Click Handler & Route Planning API Call ───────────────
// When the user clicks on the map, add a destination marker with a popup.
function handleMapClick(e) {
    // If the user is moving the marker, ignore the click event.
    if(markerState.isMoving) {
        return;
    }

    const features = map.queryRenderedFeatures(e.point, {
      layers: ["station-circles-layer", 'job-circles-layer', 'hotel-circles-layer', 'bank-circles-layer'],
    });
    if (features && features.length > 0) {
      // If the click hit a station circle, do nothing.
      return;
    }
    console.log({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    const clickedCoords = { lat: e.lngLat.lat, lng: e.lngLat.lng };
  
    addDestinationMarker(clickedCoords);
  
}

watch(centerOnMarker, (newValue) => {
    toggleCenterOnMarker(newValue);
});

function toggleCenterOnMarker(centerOnMarker) {
    if (centerOnMarker) {
        suppressMapEvents();
        const markerLngLat = personMarkerRef.value.getLngLat();
        map.setCenter(markerLngLat);
    }
}

// ───── Off-screen marker indicator ─────
const markerOffScreen = reactive({
    isOffScreen: false,
    indicatorX: 0,
    indicatorY: 0,
    angle: 0
});

// Performance optimization: throttle updates
let lastOffScreenUpdate = 0;
const OFF_SCREEN_UPDATE_INTERVAL = 100; // Update every 100ms instead of every frame

const mapMoved = ref(0);
const personScreenPosition = computed(() => {
    // Adding mapMoved.value as a dependency forces recomputation when the map moves.
    mapMoved.value;
    if (!map || !personMarkerRef.value || !mapContainer.value) return { x: 0, y: 0 };
    const pos = map.project(personMarkerRef.value.getLngLat());
    const rect = mapContainer.value.getBoundingClientRect();
    return { x: rect.left + pos.x, y: rect.top + pos.y };
});

const optionsPositions = computed(() => {
    const radius = 50; // radius in pixels
    const center = personScreenPosition.value;
    return personOptions.map((option) => {
        const rad = option.angle * (Math.PI / 180);
        return {
            label: option.label,
            x: center.x + radius * Math.cos(rad),
            y: center.y + radius * Math.sin(rad),
        };
    });
});

// ───── Off-screen marker indicator functions ─────
function updateOffScreenIndicator() {
    // Performance optimization: throttle updates to reduce CPU usage
    const now = performance.now();
    if (now - lastOffScreenUpdate < OFF_SCREEN_UPDATE_INTERVAL) {
        return;
    }
    lastOffScreenUpdate = now;
    
    if (!map || !personMarkerRef.value) return;
    
    const markerLngLat = personMarkerRef.value.getLngLat();
    const bounds = map.getBounds();
    
    // Check if marker is within visible bounds
    const isVisible = bounds.contains(markerLngLat);
    
    if (isVisible) {
        // Only update if state actually changed to avoid unnecessary reactivity
        if (markerOffScreen.isOffScreen) {
            markerOffScreen.isOffScreen = false;
        }
        return;
    }
    
    // Marker is off-screen, calculate indicator position
    if (!markerOffScreen.isOffScreen) {
        markerOffScreen.isOffScreen = true;
    }
    
    // Cache these calculations to avoid repeated DOM queries
    const mapContainer = map.getContainer();
    const containerRect = mapContainer.getBoundingClientRect();
    
    // Project marker coordinates to screen coordinates
    const markerPoint = map.project(markerLngLat);
    
    // Get visible bounds in pixel coordinates
    const topLeft = map.project(bounds.getNorthWest());
    
    // Calculate direction from center of screen to marker
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;
    
    const dx = markerPoint.x - (topLeft.x + centerX);
    const dy = markerPoint.y - (topLeft.y + centerY);
    
    // Calculate angle (0 degrees = up, 90 = right, 180 = down, 270 = left)
    const angle = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
    
    // Calculate indicator position (on the edge of the screen with some margin)
    const baseMargin = 40;
    const navbarHeight = 88; // Approximate navbar height (adjust as needed)
    
    // Add extra margin at the top to avoid navbar overlap
    const topMargin = baseMargin + navbarHeight;
    
    // Determine if the indicator would be in the top area (roughly 315° to 45°)
    const isInTopArea = angle >= 315 || angle <= 45;
    const margin = isInTopArea ? topMargin : baseMargin;
    
    // Calculate distance from center, accounting for the adjusted margin
    const maxDistance = isInTopArea 
        ? Math.min(centerX, centerY - navbarHeight/2) - baseMargin
        : Math.min(centerX, centerY) - baseMargin;
    
    const radians = (angle - 90) * Math.PI / 180; // Convert to radians and adjust for coordinate system
    const indicatorX = centerX + Math.cos(radians) * maxDistance;
    const indicatorY = centerY + Math.sin(radians) * maxDistance;
    
    // Apply the extra top margin if needed
    const finalIndicatorY = isInTopArea 
        ? Math.max(indicatorY, margin) 
        : indicatorY;
    
    // Update reactive values (batch updates to minimize reactivity overhead)
    markerOffScreen.indicatorX = indicatorX + containerRect.left;
    markerOffScreen.indicatorY = finalIndicatorY + containerRect.top;
    markerOffScreen.angle = angle;
}

function centerOnPlayer() {
    if (!map || !personMarkerRef.value) return;

    // If the player is travelling switch "center on marker" on
    if(markerState.isMoving) {
        centerOnMarker.value = true;
        return;
    }
    
    const markerLngLat = personMarkerRef.value.getLngLat();
    map.flyTo({
        center: markerLngLat,
        zoom: Math.max(map.getZoom(), 14)
    });
}

onMounted(() => {
    window.addEventListener("keydown", handleKeyDown);
    
    // Add page visibility listeners to pause/resume game when tab becomes inactive/active
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("blur", handleWindowBlur);

    onUnmounted(() => {
        stopGameLoop();
        //map.off("move", disableCenteringOnInteraction);
        //map.off("zoom", disableCenteringOnInteraction);
        //map.off("click", handleMapClick);
        //map.off("moveend", handleMapMoveEnd);
        window.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("focus", handleWindowFocus);
        window.removeEventListener("blur", handleWindowBlur);
    });
});

function disableCenteringOnInteraction() {
    if (!suppressEvents && centerOnMarker.value) {
        console.log("User interaction detected, disabling centering on marker.");
        centerOnMarker.value = false;
    }
};

// Handle page visibility changes to pause/resume game
function handleVisibilityChange() {
    if (document.hidden) {
        pauseGame();
    } else {
        resumeGame();
    }
}

function handleWindowBlur() {
    pauseGame();
}

function handleWindowFocus() {
    resumeGame();
}

function pauseGame() {
    isGamePaused.value = true;
}

function resumeGame() {
    if (isGamePaused.value) {
        isGamePaused.value = false;
        // Reset the lastUpdateTime to prevent a large time jump
        lastUpdateTime = performance.now();
    }
}

function handleMapLoad(_map) {
    // Add a new GeoJSON source for station circles.
    if (!_map.getSource("station-circles")) {
        _map.addSource("station-circles", {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });
    
        // Add a fill layer to draw the circles.
        _map.addLayer({
            id: "station-circles-layer",
            type: "fill",
            source: "station-circles",
            layout: {},
            paint: {
                "fill-color": "#d049f2",           // Change the color as needed.
                "fill-opacity": ["get", "opacity"],
            },
        });
    }

    // Add a click event for the circle layer.
    _map.on("click", "station-circles-layer", (e) => {
        if (e.features && e.features.length > 0) {
            const stationId = e.features[0].properties.id;
            const station = cache.stations.get(stationId); 
            handleStationCircleClick(station);
        }
    });
      
    // Change the cursor to a pointer when over the circle.
    const stationTooltip = document.createElement('div');
    stationTooltip.id = 'station-tooltip';
    stationTooltip.style.position = 'absolute';
    stationTooltip.style.padding = '5px 10px';
    stationTooltip.style.background = 'rgba(0, 0, 0, 0.8)';
    stationTooltip.style.color = '#fff';
    stationTooltip.style.borderRadius = '4px';
    stationTooltip.style.pointerEvents = 'none'; // So it doesn't block mouse events.
    stationTooltip.style.display = 'none';
    stationTooltip.style.zIndex = '49'; // Ensure it appears above the map.
    stationTooltip.innerText = 'Enter station';
    document.body.appendChild(stationTooltip);

    // When the mouse enters the station circle layer, show the tooltip.
    _map.on('mouseenter', 'station-circles-layer', (e) => {
        _map.getCanvas().style.cursor = 'pointer';
        stationTooltip.style.display = 'block';
    });

    // Update tooltip position on mouse move.
    _map.on('mousemove', 'station-circles-layer', (e) => {
        // e.point contains the [x,y] pixel coordinates of the mouse relative to the map.
        stationTooltip.style.left = e.point.x + 10 + 'px';
        stationTooltip.style.top = e.point.y + 10 + 'px';
    });

    // Hide tooltip when the mouse leaves the station circle layer.
    _map.on('mouseleave', 'station-circles-layer', (e) => {
        _map.getCanvas().style.cursor = '';
        stationTooltip.style.display = 'none';
    });

    if (!_map.getSource("job-circles")) {
        _map.addSource("job-circles", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });
        _map.addLayer({
            id: "job-circles-layer",
            type: "fill",
            source: "job-circles",
            layout: {},
            paint: {
                "fill-color": "#00FFFF", // Cyan for jobs
                "fill-opacity": ["get", "opacity"],
            },
        });
    }

    // Add a click event for the circle layer.
    _map.on("click", "job-circles-layer", (e) => {
        if (e.features && e.features.length > 0) {
            const jobId = e.features[0].properties.id;
            const job = cache.jobs.get(jobId);
            //console.log(jobId);
            //console.log(cache.jobs);
            //console.log(jobs);
            //const job = jobs.find((j) => j.id === jobId);
            console.log(job);
            if (job) {
                openJobMenu(job);
            }
        }
    });

    const jobTooltip = document.createElement('div');
    jobTooltip.id = 'job-tooltip';
    jobTooltip.style.position = 'absolute';
    jobTooltip.style.padding = '5px 10px';
    jobTooltip.style.background = 'rgba(0, 0, 0, 0.8)';
    jobTooltip.style.color = '#fff';
    jobTooltip.style.borderRadius = '4px';
    jobTooltip.style.pointerEvents = 'none';
    jobTooltip.style.display = 'none';
    jobTooltip.style.zIndex = '49';
    jobTooltip.innerText = 'Enter job';
    document.body.appendChild(jobTooltip);
    
    _map.on('mouseenter', 'job-circles-layer', (e) => {
        _map.getCanvas().style.cursor = 'pointer';
        jobTooltip.style.display = 'block';
    });

    _map.on('mousemove', 'job-circles-layer', (e) => {
        jobTooltip.style.left = e.point.x + 10 + 'px';
        jobTooltip.style.top = e.point.y + 10 + 'px';
    });

    _map.on('mouseleave', 'job-circles-layer', (e) => {
        _map.getCanvas().style.cursor = '';
        jobTooltip.style.display = 'none';
    });



    if (!_map.getSource("hotel-circles")) {
        _map.addSource("hotel-circles", {
        type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });
        _map.addLayer({
            id: "hotel-circles-layer",
            type: "fill",
            source: "hotel-circles",
            layout: {},
            paint: {
                "fill-color": "#FFD700", // Gold color for hotels
                "fill-opacity": ["get", "opacity"],
            },
        });
    }

    // When the hotel circle layer is clicked, open the hotel menu.
    _map.on("click", "hotel-circles-layer", (e) => {
        if (e.features && e.features.length > 0) {
            // Remove any existing destination marker
            if (destinationMarker) {
                destinationMarker.remove();
                destinationMarker = null;
            }
            
            const hotelId = e.features[0].properties.id;
            const hotel = cache.hotels.get(hotelId);
            if (canInteract(hotel)) {
                selectedHotel.value = hotel;
                showHotelMenu.value = true;
            }
        }
    });

    const hotelTooltip = document.createElement('div');
    hotelTooltip.id = 'hotel-tooltip';
    hotelTooltip.style.position = 'absolute';
    hotelTooltip.style.padding = '5px 10px';
    hotelTooltip.style.background = 'rgba(0, 0, 0, 0.8)';
    hotelTooltip.style.color = '#fff';
    hotelTooltip.style.borderRadius = '4px';
    hotelTooltip.style.pointerEvents = 'none';
    hotelTooltip.style.display = 'none';
    hotelTooltip.style.zIndex = '49';
    hotelTooltip.innerText = 'Enter hotel';
    document.body.appendChild(hotelTooltip);

    _map.on('mouseenter', 'hotel-circles-layer', (e) => {
        _map.getCanvas().style.cursor = 'pointer';
        hotelTooltip.style.display = 'block';
    });

    _map.on('mousemove', 'hotel-circles-layer', (e) => {
        hotelTooltip.style.left = e.point.x + 10 + 'px';
        hotelTooltip.style.top = e.point.y + 10 + 'px';
    });

    _map.on('mouseleave', 'hotel-circles-layer', (e) => {
        _map.getCanvas().style.cursor = '';
        hotelTooltip.style.display = 'none';
    });









    if (!_map.getSource("bank-circles")) {
        _map.addSource("bank-circles", {
        type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });
        _map.addLayer({
            id: "bank-circles-layer",
            type: "fill",
            source: "bank-circles",
            layout: {},
            paint: {
                "fill-color": "#FFD700", // Gold color for hotels
                "fill-opacity": ["get", "opacity"],
            },
        });
    }

    // When the bank circle layer is clicked, open the bank menu.
    _map.on("click", "bank-circles-layer", (e) => {
        if (e.features && e.features.length > 0) {
            // Remove any existing destination marker
            if (destinationMarker) {
                destinationMarker.remove();
                destinationMarker = null;
            }
            
            const bankId = e.features[0].properties.id;
            const bank = cache.banks.get(bankId);
            if (canInteract(bank)) {
                selectedBank.value = bank;
                showBankMenu.value = true;
            }
        }
    });

    const bankTooltip = document.createElement('div');
    bankTooltip.id = 'bank-tooltip';
    bankTooltip.style.position = 'absolute';
    bankTooltip.style.padding = '5px 10px';
    bankTooltip.style.background = 'rgba(0, 0, 0, 0.8)';
    bankTooltip.style.color = '#fff';
    bankTooltip.style.borderRadius = '4px';
    bankTooltip.style.pointerEvents = 'none';
    bankTooltip.style.display = 'none';
    bankTooltip.style.zIndex = '49';
    bankTooltip.innerText = 'Enter bank';
    document.body.appendChild(bankTooltip);

    _map.on('mouseenter', 'bank-circles-layer', (e) => {
        _map.getCanvas().style.cursor = 'pointer';
        bankTooltip.style.display = 'block';
    });

    _map.on('mousemove', 'bank-circles-layer', (e) => {
        bankTooltip.style.left = e.point.x + 10 + 'px';
        bankTooltip.style.top = e.point.y + 10 + 'px';
    });

    _map.on('mouseleave', 'bank-circles-layer', (e) => {
        _map.getCanvas().style.cursor = '';
        bankTooltip.style.display = 'none';
    });
}

function handleMapMove() {
    mapMoved.value++;
    disableCenteringOnInteraction();
}
  
// ───── Placeholder: Station fetching on moveend ─────────────
// (This is carried over from the previous version.)
function handleMapMoveEnd() {
    updateAllMarkers();
    
    // Update off-screen indicator immediately when map stops moving (more responsive)
    lastOffScreenUpdate = 0; // Reset throttle to allow immediate update
    updateOffScreenIndicator();
  
    const currentBounds = map.getBounds();
    const currentZoom = map.getZoom();
  
    // Check if we haven't fetched stations, if the current bounds exceed the cached expanded bounds,
    // or if the zoom level has changed.
    if (
        !lastFetchedBounds ||
        currentZoom !== lastFetchedZoom ||
        !lastFetchedBounds.contains(currentBounds.getNorthEast()) ||
        !lastFetchedBounds.contains(currentBounds.getSouthWest())
    ) {
        fetchStations();
        fetchJobs();
        fetchHotels();
        fetchBanks();
    }
}

function handleMapZoom() {
    disableCenteringOnInteraction();

    const currentZoom = map.getZoom();
    showStationsFiltered.value = currentZoom < 12;
    markersHiddenDueToZoom.value = currentZoom < 10;
    updateAllMarkers();
}

// Add reactive map state
const mapState = reactive({
     zoom: 0
});

// Replace the computed property with a ref
const showStationsFiltered = ref(false);
const markersHiddenDueToZoom = ref(false);

// Generic function to fetch data based on type
async function fetchData(type) {
    if (!map || isFetching[type] || markersHiddenDueToZoom.value) return;
    isFetching[type] = true;
    
    // Update the filtered state when fetching stations
    //if (type === 'stations') {
        //showStationsFiltered.value = map.getZoom() < 12;
    //}
    const bounds = map.getBounds();
    const center = bounds.getCenter();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latDiff = ne.lat - sw.lat;
    const lngDiff = ne.lng - sw.lng;
    
    // Calculate the expanded bounds (i.e. 3× the visible area)
    const expandedSw = { lat: center.lat - latDiff * 1.5, lng: center.lng - lngDiff * 1.5 };
    const expandedNe = { lat: center.lat + latDiff * 1.5, lng: center.lng + lngDiff * 1.5 };

    // Remove items outside expanded bounds before adding new ones
    cache[type].forEach((item, id) => {
        const point = [item.lng, item.lat];
        if (lastFetchedBounds && !lastFetchedBounds.contains(point)) {
            cache[type].delete(id);
        }
    });
    
    // Save the expanded bounds as a Mapbox LngLatBounds object
    lastFetchedBounds = new mapboxgl.LngLatBounds(
        [expandedSw.lng, expandedSw.lat],
        [expandedNe.lng, expandedNe.lat]
    );
    lastFetchedZoom = map.getZoom();
    
    // Construct the API URL with query parameters
    let url = `${baseUrl}api/${type}?swLat=${expandedSw.lat}&swLng=${expandedSw.lng}&neLat=${expandedNe.lat}&neLng=${expandedNe.lng}`;
    
    // Add filter parameter for stations when zoom level is low
    //if (type === 'stations' && map.getZoom() < 12) {
        //url += '&filter=1';
    //}
    if (type === 'stations') {
        url += '&filter=1,3,4,5,9';
    }
    
    try {
        const response = await fetch(url);
        const data = await response.json();
        
        // Merge data into our cache (avoiding duplicates)
        data.data.forEach((item) => {
            if (!cache[type].has(item.id)) {
                cache[type].set(item.id, item);
            }
        });
        
        // Update markers and circles based on type
        switch (type) {
        case 'stations':
            updateStationMarkersAndCircles();
            break;
        case 'jobs':
            updateJobMarkersAndCircles();
            break;
        case 'hotels':
            updateHotelMarkersAndCircles();
            break;
        case 'banks':
            updateBankMarkersAndCircles();
            break;
        }
    } catch (error) {
        console.error(`Error fetching ${type}:`, error);
    } finally {
        isFetching[type] = false;
    }
}

// Update the individual fetch functions to use the generic one
async function fetchStations() {
    await fetchData('stations');
}

async function fetchJobs() {
    await fetchData('jobs');
}

async function fetchHotels() {
    await fetchData('hotels');
}

async function fetchBanks() {
    await fetchData('banks');
}

// ───── Helper: Generate a Circle Polygon (200m radius) ─────────────────────
function generateCirclePolygon(center, radius, points = 64) {
    const coords = [];
    const earthRadius = 6371000; // meters
    const centerLatRad = center[1] * Math.PI / 180;
    const centerLngRad = center[0] * Math.PI / 180;
    for (let i = 0; i <= points; i++) {
        const angle = (i * 2 * Math.PI) / points;
        // Calculate offsets in radians
        const offsetLat = (radius * Math.sin(angle)) / earthRadius;
        const offsetLng = (radius * Math.cos(angle)) / (earthRadius * Math.cos(centerLatRad));
        const newLat = centerLatRad + offsetLat;
        const newLng = centerLngRad + offsetLng;
        coords.push([newLng * 180 / Math.PI, newLat * 180 / Math.PI]);
    }
    return {
        type: "Feature",
        geometry: {
            type: "Polygon",
            coordinates: [coords],
        },
    };
}

// ───── Handle Clicks on the Station Circle ────────────────────────────────
function handleStationCircleClick(station) {
    if (!canInteract(station)) return;
    selectedStation.value = station;
    if(Number(station.type) === 1) {
        showTrainTimeTable.value = true;
    }

    if([2, 4].includes(Number(station.type))) {
        showBusTimeTable.value = true;
    }

    if(Number(station.type) === 5) {
        showBoatTimeTable.value = true;
    }

    if(Number(station.type) === 9) {
        showMultiTimeTable.value = true;
    }
}

// Helper functions to determine if markers should be visible based on game settings
function shouldShowStation(station) {
    return stationVisible(station, gameSettings.value);
}

function shouldShowJob(job) {
    return gameSettings.value.showJobs;
}

function shouldShowHotel(hotel) {
    return gameSettings.value.showHotels;
}

function shouldShowBank(bank) {
    return gameSettings.value.showBanks;
}

function updateMarkersForType(type, createMarkerCallback) {
    if (!map) return;
    const currentBounds = map.getBounds();
    const items = Array.from(cache[type].values());
    
    // Helper function to check if item should be visible
    const shouldShow = (item) => {
        if (markersHiddenDueToZoom.value) return false;

        switch(type) {
            case 'stations':
                return shouldShowStation(item);
            case 'jobs':
                return shouldShowJob(item);
            case 'hotels':
                return shouldShowHotel(item);
            case 'banks':
                return shouldShowBank(item);
            default:
                return true;
        }
    };
  
    items.forEach((item) => {
        const point = [item.lng, item.lat];
        const withinBounds = currentBounds.contains(point);
        const shouldBeVisible = shouldShow(item);
        
        if (withinBounds && shouldBeVisible) {
            // Add marker if it should be visible and doesn't exist
            if (!markers[type].has(item.id)) {
                const markerEl = createMarkerCallback(item);
                const marker = new mapboxgl.Marker(markerEl)
                    .setLngLat(point)
                    .addTo(map);
                markers[type].set(item.id, marker);
            }
        } else {
            // Remove marker if it exists but shouldn't be visible (either out of bounds or hidden)
            if (markers[type].has(item.id)) {
                markers[type].get(item.id).remove();
                markers[type].delete(item.id);
            }
        }
    });
}

function updateProximityCircles(type, sourceName, getRadiusCallback, getVisibleDistanceMinCallback, getVisibleDistanceMaxCallback) {
    if (!map || !personMarkerRef.value) return;
    const personPos = personMarkerRef.value.getLngLat();
    const personCoord = [personPos.lng, personPos.lat];
    const features = [];
    
    // Helper function to check if item should be visible
    const shouldShow = (item) => {
        if (markersHiddenDueToZoom.value || markerState.isMoving) return false;
        switch(type) {
            case 'stations':
                return shouldShowStation(item);
            case 'jobs':
                return shouldShowJob(item);
            case 'hotels':
                return shouldShowHotel(item);
            case 'banks':
                return shouldShowBank(item);
            default:
                return true;
        }
    };
  
    Array.from(cache[type].values()).forEach((item) => {
        // Only create circles for items that should be visible
        if (!shouldShow(item)) return;
        
        const itemCoord = [item.lng, item.lat];
        const distance = calculateDistance(personCoord, itemCoord);
        let opacity = 0;
        const visibleDistanceMin = getVisibleDistanceMinCallback(item);
        const visibleDistanceMax = getVisibleDistanceMaxCallback(item);
        const radius = getRadiusCallback(item);

        // When within 400m, circle is fully visible; then fade out by 600m.
        if (distance <= visibleDistanceMin) {
            opacity = 0.7;
        } else if (distance > visibleDistanceMin && distance <= visibleDistanceMax) {
            opacity = 0.7 - ((distance - visibleDistanceMin) / radius);
        }

        if (opacity > 0) {
            let circleFeature = generateCirclePolygon(itemCoord, radius);
            circleFeature.properties = circleFeature.properties || {};
            circleFeature.properties.opacity = opacity;
            circleFeature.properties.id = item.id;
            features.push(circleFeature);
        }
    });
    
    const geojson = { type: "FeatureCollection", features };
    const circleSource = map.getSource(sourceName);
    if (circleSource) {
        circleSource.setData(geojson);
    }
}

function updateStationMarkersAndCircles(updateMarkers = true, updateCircles = true) {
    if (updateMarkers) {
        updateMarkersForType('stations', createStationMarkerElement);
    }
    if (updateCircles) {
        updateProximityCircles(
            'stations',
            "station-circles",
            () => INTERACTION_DISTANCE,
            () => INTERACTION_DISTANCE,
            () => INTERACTION_DISTANCE
        );
    }
}

function updateSingleStationMarker(stationId) {
    if (!map) return;
    const station = cache.stations.get(stationId);
    if (!station) return;

    // Remove existing marker if present
    if (markers.stations.has(stationId)) {
        markers.stations.get(stationId).remove();
        markers.stations.delete(stationId);
    }

    // Only add marker if within current map bounds AND should be visible
    const currentBounds = map.getBounds();
    const point = [station.lng, station.lat];
    if (currentBounds.contains(point) && shouldShowStation(station)) {
        const markerEl = createStationMarkerElement(station);
        const marker = new mapboxgl.Marker(markerEl)
            .setLngLat(point)
            .addTo(map);
        markers.stations.set(stationId, marker);
    }
}

function updateJobMarkersAndCircles(updateMarkers = true, updateCircles = true) {
    if (updateMarkers) {
        updateMarkersForType('jobs', createJobMarkerElement);
    }
    if (updateCircles) {
        updateProximityCircles(
            'jobs',
            "job-circles",
            () => 50,
            () => 75,
            () => 150
        );
        }
}

function updateHotelMarkersAndCircles(updateMarkers = true, updateCircles = true) {
    if (updateMarkers) {
        updateMarkersForType('hotels', createHotelMarkerElement);
    }
    if (updateCircles) {
        updateProximityCircles(
            'hotels',
            "hotel-circles",
            () => 50,
            () => 75,
            () => 150
        );
    }
}

function updateBankMarkersAndCircles(updateMarkers = true, updateCircles = true) {
    if (updateMarkers) {
        updateMarkersForType('banks', createBankMarkerElement);
    }
    if (updateCircles) {
        updateProximityCircles(
            'banks',
            "bank-circles",
            () => 50,
            () => 75,
            () => 150
        );
    }
}

function updateAllMarkers(updateMarkers = true, updateCircles = true) {
    updateStationMarkersAndCircles(updateMarkers, updateCircles);
    updateJobMarkersAndCircles(updateMarkers, updateCircles);
    updateHotelMarkersAndCircles(updateMarkers, updateCircles);
    updateBankMarkersAndCircles(updateMarkers, updateCircles);
}


let previousGameSpeed = gameSpeed.value;
// Called when "Start work" is clicked in JobMenu.
// Receives an object { penalty } where penalty is 1 for an on-time start
// or 0.75 if the job is started late.
function handleStartJob({ penalty }) {
    if (!canInteract(selectedJob.value)) return;
    previousGameSpeed = gameSpeed.value;

    fastForwardToTime(secondsToTimeString(timeStringToSeconds(selectedJob.value.end) - timeZoneOffset.value));
    completeJob(penalty);
}

function completeJob(penalty) {
    let audio = new Audio("sounds/tada.mp3");
    if(selectedJob.value.name == "Lee Ho Fook") {
        audio = new Audio("sounds/werewolves.mp3");
    }
    audio.volume = muted.value ? 0 : 0.8;
    audio.play();
    
    // Increase the user's money.
    // For example, if state.money is an array of currency objects:
    const bonus = selectedJob.value.money * penalty;
    addMoney(bonus, selectedJob.value.currency, selectedJob.value.currency_symbol);

    logGame({
        event: `Job`,
        event_id: selectedJob.value.id,
        from_lat: personMarkerRef.value.getLngLat().lat,
        from_lng: personMarkerRef.value.getLngLat().lng,
        to_lat: personMarkerRef.value.getLngLat().lat,
        to_lng: personMarkerRef.value.getLngLat().lng,
        money: bonus,
        currency: selectedJob.value.currency
    });

    
    // Remove the job from the cache and its marker from the map
    const jobId = selectedJob.value.id;
    if (cache.jobs.has(jobId)) {
        cache.jobs.delete(jobId);
    }
    if (markers.jobs.has(jobId)) {
        markers.jobs.get(jobId).remove();
        markers.jobs.delete(jobId);
    }
    
    // Update the job circles to remove the completed job's circle
    updateJobMarkersAndCircles(false, true);
    
    // Reset game speed to the previous value.
    gameSpeed.value = previousGameSpeed;
    
    // Optionally hide the JobMenu.
    showJobMenu.value = false;
    showJobInProgress.value = false;

    notify({
        title: 'Job complete',
        text: `You have successfully completed your job at ${selectedJob.value.name}!`,
        type: 'success',
        duration: 5000
    })

    selectedJob.value = null;
}

function scheduleDeparture(data, type) {
    console.log(data);
    const { station, transport, route, stops } = data;
    const departureTimeSeconds = departureTime(gameTime.value, timeZoneOffset.value, timeStringToSeconds(transport.depart), transport.depart_delay_days);
    if (!canInteract(station) || !validRoute(route) || !(Number(transport.duration_seconds) > 0) || departureTimeSeconds < gameTime.value) {
        notify({ title: 'Ticket unavailable', text: 'The journey could not be loaded or has already departed. Please refresh the timetable.', type: 'error' });
        return;
    }
    const departureStationCoords = [station.lng, station.lat];

    // Deduct the journey cost from the user's wallet.
    const result = deductMoney(transport.price, transport.currency_code);
    if(!result) {
        const audio = new Audio("sounds/beep-warning.mp3");
        audio.volume = muted.value ? 0 : 0.5; // Set volume to 50%
        audio.play();

        notify({
            title: `Insufficient Funds`,
            text: `You do not have enough money to purchase this ${type} ticket.`,
            type: "error",
            duration: 5000
        });
        return;
    };

    showTrainTimeTable.value = false;
    showBusTimeTable.value = false;
    showBoatTimeTable.value = false;
    showMultiTimeTable.value = false;

    notify({
        title: `Ticket confirmed`,
        text: `You have successfully purchased a ${type} ticket to <strong>${transport.to_station_name}</strong> leaving at <strong>${transport.depart}</strong> for <strong>${transport.currency_symbol}${transport.price.toFixed(2)}</strong>`,
        type: "success",
        duration: 5000
    });

    const audio = new Audio("sounds/kerching.mp3");
    audio.volume = muted.value ? 0 : 0.5; // Set volume to 50%
    audio.play();
    

    // Add to scheduled stations for marker highlighting and proximity message
    scheduledStations.value.push({
        lng: station.lng,
        lat: station.lat,
        stationId: station.id,
        name: station.name || "station",
        type,
        depart: transport.depart,
        depart_delay_days: transport.depart_delay_days,
        triggerTime: departureTimeSeconds,
    });

    updateSingleStationMarker(station.id);

    //markerState.routeCompleted = false;

    logGame({
        event: `${capitalizeWords(type)} Ticket`,
        event_id: transport.id,
        source: transport.source || '',
        from_lat: personMarkerRef.value.getLngLat().lat,
        from_lng: personMarkerRef.value.getLngLat().lng,
        from_id: transport.from_id,
        to_id: transport.to_id,
        money: transport.price,
        currency: transport.currency_code
    });

    scheduledEvents.push({
        triggerTime: departureTimeSeconds,
        callback: () => {
            const playerPos = personMarkerRef.value.getLngLat();
            const playerCoords = [playerPos.lng, playerPos.lat];
            const distance = calculateDistance(playerCoords, departureStationCoords);

            if (distance <= INTERACTION_DISTANCE && !markerState.isMoving) {
                cancelRoute();
                hoveredMarker.value = null;
                const travelTimeInSeconds = transport.duration_seconds || 1800;

                // Remove the scheduled station from the list after the event triggers.
                scheduledStations.value = scheduledStations.value.filter(
                    s => s.stationId !== station.id
                );

                logGame({
                    event: capitalizeWords(type),
                    event_id: transport.id,
                    source: transport.source || '',
                    from_lat: personMarkerRef.value.getLngLat().lat,
                    from_lng: personMarkerRef.value.getLngLat().lng,
                    from_id: transport.from_id,
                    to_id: transport.to_id,
                });

                // Change person marker to an image
                updatePersonMarkerForTransport(transport.carrier_image);

                addConfirmedRouteLayers();
                moveMarkerAlongRoute(personMarkerRef.value, route, travelTimeInSeconds, stops, map, type);

                if (map.getZoom() > 9) {
                    map.setZoom(9);
                }
                
                centerOnMarker.value = true;

                // 2) now calculate zone‐change events:
                const journeyStart = markerState.startTime;
                const transitions = getZoneTransitions([station, ...stops])
                console.log(transitions);
                transitions.forEach(({ stopTime,  newZone }) => {
                    console.log(stopTime);
                    scheduledEvents.push({
                        triggerTime: gameTime.value + stopTime,
                        callback: () => {
                            if (markerState.hasGotOff || markerState.startTime !== journeyStart) return;
                            const countryCode = getCountryCodeForTimezone(newZone);
                            console.log(timeZone.value, newZone);
                            timeZoneOffset.value += diffZonesInMinutes(timeZone.value, newZone) * 60;
                            lastTimeZoneOffset.value = diffZonesInMinutes(timeZone.value, newZone) * 60;
                            timeZone.value = newZone;
                            if (!muted.value) playNationalAnthem(countryCode);
                            
                            currentCountryCode.value = countryCode;

                            countryNotification.value = true;

                            logGame({
                                event: 'Country Visit',
                                from_lat: personMarkerRef.value.getLngLat().lat,
                                from_lng: personMarkerRef.value.getLngLat().lng,
                                to_lat: personMarkerRef.value.getLngLat().lat,
                                to_lng: personMarkerRef.value.getLngLat().lng,
                                start: gameTime.value,
                                end: gameTime.value,
                            });

                            countriesVisited.value.push(countryCode);

                            setTimeout(() => {
                                countryNotification.value = false;
                            }, 8000);

                        }
                    })
                })
            } else {
                notify({
                    title: `Missed ${type}`,
                    text: `You were too far from the station and missed your ${type}.`,
                    type: "error",
                    duration: 5000
                });

                // Play warning sound
                const audio = new Audio("sounds/beep-warning.mp3");
                audio.volume = muted.value ? 0 : 0.5;
                audio.play();
            }

            // Remove from scheduled stations after event triggers
            scheduledStations.value = scheduledStations.value.filter(
                s => s.stationId !== station.id
            );
            // Remove the marker for the missed bus station
            updateSingleStationMarker(station.id);
        },
        departureStationCoords
    });
}

function updatePersonMarkerForTransport(carrier_image) {
    if (!personMarkerRef.value) return;
    const el = personMarkerRef.value.getElement();
    if (carrier_image) {
        el.classList.remove("player-marker");
        el.classList.add("transport-marker");

        el.style.backgroundImage = `url('${carrier_image}')`;
    } else {
        el.classList.remove("transport-marker");
        el.classList.add("player-marker");

        //Remove background image
        el.style.backgroundImage = "";
    }
}

function getZoneTransitions(stops) {
    // turn each stop’s lat/lng into a zone string
    const zones = stops.map(s => tzLookup(
        parseFloat(s.lat),
        parseFloat(s.lng)
    ))
    // find every index where it differs from the previous
    const transitions = []
    for (let i = 1; i < zones.length; i++) {
        if (zones[i] !== zones[i - 1]) {
            transitions.push({ stopIndex: i, stopTime: stops[i].duration_seconds, newZone: zones[i] })
        }
    }
    return transitions
}
  
function fastForwardToTime(wakeUpTime) {
    // Convert a "HH:MM" string to seconds.
    const [hours, minutes] = wakeUpTime.split(":").map(Number);
    const targetSeconds = hours * 3600 + minutes * 60;
    // If current gameTime mod 86400 is already past the target, assume next day.
    const secondsToday = gameTime.value % 86400;

    // Play wake-up sound if crossing 6am threshold
    // Case 1: Same day - current time is before 6am and wake-up time is after 6am
    // Case 2: Next day - waking up after 6am tomorrow
    const shouldPlayWakeUpSound = (secondsToday < targetSeconds && secondsToday < 21600 && targetSeconds >= 21600) || 
                                  (secondsToday >= targetSeconds && targetSeconds >= 21600);
    
    if (shouldPlayWakeUpSound) {
        const wakeUpSound = new Audio("sounds/cock-a-doodle-doo.mp3");
        //wakeUpSound.play();
    }

    if (secondsToday < targetSeconds) {
        gameTime.value = gameTime.value - secondsToday + targetSeconds;
    } else {
        gameTime.value = gameTime.value - secondsToday + targetSeconds + 86400;
    }

}

function handleHotelStay(wakeUpTime, hotel) {
    if (!canInteract(hotel)) return;
    // Deduct the hotel price from the user's wallet.
    const price = hotel.price; // assume your hotel API returns a price field

    const canDeduct = deductMoney(price, hotel.currency_code);

    if (!canDeduct) {
        return;
    }

    const startTime = gameTime.value;

    // Okay here we actually need to minus the current timezone offset
    fastForwardToTime(secondsToTimeString(timeStringToSeconds(wakeUpTime) - timeZoneOffset.value));
    lastDaySlept = Math.floor((gameTime.value + timeZoneOffset.value) / 86400);
    // Once the wake-up time is reached, mark that the player is no longer in hotel.
    // (This could be done via an event listener or additional logic.)

    logGame({
        event: 'Hotel',
        from_lat: personMarkerRef.value.getLngLat().lat,
        from_lng: personMarkerRef.value.getLngLat().lng,
        to_lat: personMarkerRef.value.getLngLat().lat,
        to_lng: personMarkerRef.value.getLngLat().lng,
        start: startTime,
        end: gameTime.value,
        money: price,
        currency: hotel.currency_code,
    });

    showHotelMenu.value = false;
}

function dismissAutoSleep() {
    showAutoSleepDialog.value = false;
    // Resume the game loop when the dialog is dismissed.
    startGameLoop();
}

// Update marker visibility based on settings.
// For each marker cache, simply toggle the marker's element display.
// Each station is filtered by its own transport type in updateAllMarkers.

function toggleStationsVisibility(visible) {
    markers.stations.forEach(marker => {
        marker.getElement().style.display = visible ? "block" : "none";
    });

    if (map && map.getLayer("station-circles-layer")) {
        map.setLayoutProperty("station-circles-layer", "visibility", visible ? "visible" : "none");
    }
}

watch(() => gameSettings.value.showJobs, (newVal) => {
    toggleJobsVisibility(newVal);
});

function toggleJobsVisibility(visible) {
    markers.jobs.forEach(marker => {
        marker.getElement().style.display = visible ? "block" : "none";
    });

    if (map && map.getLayer("job-circles-layer")) {
        map.setLayoutProperty("job-circles-layer", "visibility", visible ? "visible" : "none");
    }
}

watch(() => gameSettings.value.showHotels, (newVal) => {
    toggleHotelsVisibility(newVal);
});

function toggleHotelsVisibility(visible) {
    markers.hotels.forEach(marker => {
        marker.getElement().style.display = visible ? "block" : "none";
    });

    if (map && map.getLayer("hotel-circles-layer")) {
        map.setLayoutProperty("hotel-circles-layer", "visibility", visible ? "visible" : "none");
    }
}

watch(() => gameSettings.value.showBanks, (newVal) => {
    toggleBankVisibility(newVal);
});

watch(gameSettings, () => {
    updateAllMarkers();
}, { deep: true });

function toggleBankVisibility(visible) {
    markers.banks.forEach(marker => {
        marker.getElement().style.display = visible ? "block" : "none";
    });

    if (map && map.getLayer("bank-circles-layer")) {
        map.setLayoutProperty("bank-circles-layer", "visibility", visible ? "visible" : "none");
    }
}

function handleKeyDown(e) {
    /*if(race.value && !showRegister.value) {
        if (e.key.toLowerCase() === "s") {
            toggleSettingsDialog();
        }

        if (e.key.toLowerCase() === "p") {
            togglePassportDialog();
        }

        if (e.key.toLowerCase() === "j") {
            toggleJobsDialog();
        }

        if (e.key.toLowerCase() === "w") {
            toggleWalletDialog();
        }
    }*/
}

function updateGameSettings(newSettings) {
    gameStore.updateGameSettings(newSettings);
}

// Update the computed property to use the new cache structure
//const jobs = computed(() => Array.from(cache.jobs.values()));

// Add this computed property to check if any fetching is in progress
const isLoading = computed(() => {
  return isFetching.stations || isFetching.jobs || isFetching.hotels;
});

// ───── Marker Element Creation Callbacks ─────────────────────────────────────
// For stations: show an icon (based on type) and attach a click handler that opens a popup with route options.
function createStationMarkerElement(station) {
  const el = document.createElement("div");
  el.className = "station-marker";
  const img = document.createElement("img");

  // Check if this station has a scheduled journey
  const isScheduled = scheduledStations.value.some(
    (s) => s.stationId === station.id
  );

  if (station.type == 1) {
    img.src = `${baseUrl}img/icons/${isScheduled ? 'train-scheduled.svg' : 'train.svg'}`;
    img.style.width = "30px";
    img.style.height = "30px";
  } else if (station.type == 2 || station.type == 4) {
    img.src = `${baseUrl}img/icons/${isScheduled ? 'bus-scheduled.svg' : 'bus.svg'}`;
    img.style.width = "30px";
    img.style.height = "30px";
  } else if (station.type == 3) {
    img.src = `${baseUrl}img/icons/tram.svg`;
    img.style.width = "30px";
    img.style.height = "30px";
  } else if (station.type == 5) {
    img.src = `${baseUrl}img/icons/${isScheduled ? 'ferry-scheduled.svg' : 'ferry.svg'}`;
    img.style.width = "30px";
    img.style.height = "30px";
  } else if (station.type == 9) {
    img.src = `${baseUrl}img/icons/${isScheduled ? 'multi-scheduled.svg' : 'multi.svg'}`;
    img.style.width = "30px";
    img.style.height = "30px";
  } else {
    img.src = `${baseUrl}img/icons/station.svg`;
    img.style.width = "30px";
    img.style.height = "30px";
  }
  el.appendChild(img);

  el.addEventListener('click', e => {
    e.stopPropagation();
    if (markerState.isMoving) return;
    if (canInteract(station)) handleStationCircleClick(station);
    else addDestinationMarker({ lat: station.lat, lng: station.lng });
    hoveredMarker.value = null;
  });

  // Show/hide tooltip on hover
  el.addEventListener("mouseenter", (e) => {
    e.stopPropagation();
    // Get the marker's position on the screen
    const rect = el.getBoundingClientRect();
    tooltipPosition.value = {
      x: rect.left,
      y: rect.top
    };
    hoveredMarker.value = {...station, entity: 'station', entityType: station.type};
    isHoveringMarker.value = true;
  });

  el.addEventListener("mouseleave", (e) => {
    e.stopPropagation();
    isHoveringMarker.value = false;
    setTimeout(() => {
      if (!isHoveringTooltip.value && !isHoveringMarker.value) {
        hoveredMarker.value = null;
      }
    }, 50);
  });

  return el;
}

// For jobs: use a generic job icon and attach a click handler that opens the JobMenu.
function createJobMarkerElement(job) {
    const el = document.createElement("div");
    el.className = "job-marker";
    const img = document.createElement("img");
    // Use a generic job icon.
    img.src = `${baseUrl}img/icons/job.svg`;
    img.style.width = "30px";
    img.style.height = "30px";
    el.appendChild(img);
    
    // Show/hide tooltip on hover
    el.addEventListener("mouseenter", (e) => {
        e.stopPropagation();
        // Get the marker's position on the screen
        const rect = el.getBoundingClientRect();
        tooltipPosition.value = {
            x: rect.left,
            y: rect.top
        };
        hoveredMarker.value = {...job, entity: 'job'};
        isHoveringMarker.value = true;
    });

    el.addEventListener("mouseleave", (e) => {
        e.stopPropagation();
        isHoveringMarker.value = false;
        setTimeout(() => {
            if (!isHoveringTooltip.value && !isHoveringMarker.value) {
                hoveredMarker.value = null;
            }
        }, 50);
    });

    el.addEventListener("click", (e) => {
        e.stopPropagation();
        if (markerState.isMoving) return;
        if (canInteract(job)) { openJobMenu(job); return; }
        if (destinationMarker) {
        destinationMarker.remove();
        destinationMarker = null;
        }
        const popupDiv = document.createElement("div");
        const walkButton = document.createElement("button");
        walkButton.textContent = "Walk here";
        walkButton.className = "px-2 py-1 bg-green-500 text-white rounded mr-2";
        const taxiButton = document.createElement("button");
        taxiButton.textContent = "Taxi here";
        taxiButton.className = "px-2 py-1 bg-blue-500 text-white rounded";
        popupDiv.appendChild(walkButton);
        popupDiv.appendChild(taxiButton);
        walkButton.addEventListener("click", async (ev) => {
            ev.stopPropagation();
            await planRoute("walk", { lat: job.lat, lng: job.lng });
        });
        taxiButton.addEventListener("click", async (ev) => {
            ev.stopPropagation();
            await planRoute("taxi", { lat: job.lat, lng: job.lng });
        });
        new mapboxgl.Popup({ offset: 25 })
            .setLngLat([job.lng, job.lat])
            .setDOMContent(popupDiv)
            .addTo(map);
    });
    return el;
}

function createHotelMarkerElement(hotel) {
    const el = document.createElement("div");
    el.className = "hotel-marker";
    const img = document.createElement("img");
    img.src = `${baseUrl}img/icons/hotel.svg`; // ensure you have a hotel.svg icon
    img.style.width = "30px";
    img.style.height = "30px";
    el.appendChild(img);
    
    // Show/hide tooltip on hover
    el.addEventListener("mouseenter", (e) => {
        e.stopPropagation();
        // Get the marker's position on the screen
        const rect = el.getBoundingClientRect();
        tooltipPosition.value = {
            x: rect.left,
            y: rect.top
        };
        hoveredMarker.value = {...hotel, entity: 'hotel'};
        isHoveringMarker.value = true;
    });

    el.addEventListener("mouseleave", (e) => {
        e.stopPropagation();
        isHoveringMarker.value = false;
        setTimeout(() => {
            if (!isHoveringTooltip.value && !isHoveringMarker.value) {
                hoveredMarker.value = null;
            }
        }, 50);
    });

    el.addEventListener("click", (e) => {
        e.stopPropagation();
        if (markerState.isMoving) return;
        if (!canInteract(hotel)) { addDestinationMarker(hotel); return; }
        if (destinationMarker) {
            destinationMarker.remove();
            destinationMarker = null;
        }
        selectedHotel.value = hotel;
        showHotelMenu.value = true;
    });
    return el;
}

function createBankMarkerElement(bank) {
    const el = document.createElement("div");
    el.className = "bank-marker";
    const img = document.createElement("img");
    img.src = `${baseUrl}img/icons/bank.svg`; // ensure you have a hotel.svg icon
    img.style.width = "30px";
    img.style.height = "30px";
    el.appendChild(img);
    
    // Show/hide tooltip on hover
    el.addEventListener("mouseenter", (e) => {
        e.stopPropagation();
        // Get the marker's position on the screen
        const rect = el.getBoundingClientRect();
        tooltipPosition.value = {
            x: rect.left,
            y: rect.top
        };
        hoveredMarker.value = {...bank, entity: 'bank'};
        isHoveringMarker.value = true;
    });

    el.addEventListener("mouseleave", (e) => {
        e.stopPropagation();
        isHoveringMarker.value = false;
        setTimeout(() => {
            if (!isHoveringTooltip.value && !isHoveringMarker.value) {
                hoveredMarker.value = null;
            }
        }, 50);
    });

    el.addEventListener("click", (e) => {
        e.stopPropagation();
        if (markerState.isMoving) return;
        if (!canInteract(bank)) { addDestinationMarker(bank); return; }
        if (destinationMarker) {
            destinationMarker.remove();
            destinationMarker = null;
        }
        selectedBank.value = bank;
        showBankMenu.value = true;
    });
    return el;
}

// When a job marker is clicked, open the JobMenu modal.
function canInteract(place) {
    if (!place || !personMarkerRef.value || markerState.isMoving || !game.value || hasFinished.value) return false;
    const player = personMarkerRef.value.getLngLat();
    return calculateDistance([player.lng, player.lat], [Number(place.lng), Number(place.lat)]) <= INTERACTION_DISTANCE;
}

function openJobMenu(job) {
    if (!canInteract(job)) return;
    selectedJob.value = job;
    showJobMenu.value = true;
}

// Add these refs at the top of your script setup
const hoveredMarker = ref(null);
const tooltipPosition = ref({ x: 0, y: 0 });
const isHoveringTooltip = ref(false);
const isHoveringMarker = ref(false);

function handleTooltipLeave() {
    isHoveringTooltip.value = false;
    if (!isHoveringMarker.value) {
        hoveredMarker.value = null;
    }
}

function updateProximityMessage() {
    proximityStation.value = null;
    if (markerState.isMoving || !personMarkerRef.value || scheduledStations.value.length === 0) {
        proximityMessage.value = "";
        proximityStation.value = null;
        return;
    }

    const playerPos = personMarkerRef.value.getLngLat();
    let found = false;

    for (const s of scheduledStations.value) {
        const distance = calculateDistance([playerPos.lng, playerPos.lat], [s.lng, s.lat]);

        if (distance <= INTERACTION_DISTANCE) {
            proximityMessage.value = `You are close enough to board your scheduled ${s.type} at ${s.name}.`;
            proximityStation.value = s;
            found = true;
            break;
        }
    }

    if (!found) {
        const stationNames = scheduledStations.value.map(s => s.name);

        let stationsText;
        if (stationNames.length === 1) {
            stationsText = stationNames[0];
        } else if (stationNames.length === 2) {
            stationsText = `${stationNames[0]} or ${stationNames[1]}`;
        } else {
            stationsText = `${stationNames.slice(0, -1).join(", ")} or ${stationNames.slice(-1)}`;
        }

        const prefix = stationNames.length === 1 
        ? "Move closer to" 
        : stationNames.length === 2 
            ? "Move closer to either" 
            : "Move closer to any of";

        proximityMessage.value = `${prefix} ${stationsText} to board your scheduled transport.`;
    }
}

function checkFinishMarkerProximity() {
    if (!personMarkerRef.value || !race.value?.endCoords) {
        return;
    }

    const playerPos = personMarkerRef.value.getLngLat();
    const finishCoords = race.value.endCoords;
    const distance = calculateDistance([playerPos.lng, playerPos.lat], finishCoords);
  
    // Check if player is within 50 meters of the finish marker
    const FINISH_PROXIMITY_DISTANCE = 20; // meters
  
    if (distance <= FINISH_PROXIMITY_DISTANCE) {
        if(!hasFinished.value) {
            hasFinished.value = true;
            
            // Calculate finish time
            console.log(race.value);
            finishTime.value = gameTime.value - race.value.start_time;
            
            stopGameLoop(); // Stop the game loop

            logGame({
                event: `Finish`,
                from_lat: personMarkerRef.value.getLngLat().lat,
                from_lng: personMarkerRef.value.getLngLat().lng,
                to_lat: personMarkerRef.value.getLngLat().lat,
                to_lng: personMarkerRef.value.getLngLat().lng,
                start: gameTime.value,
                end: gameTime.value,
            }, () => { finishSaved.value = true; });

            console.log(`🏁 CONGRATULATIONS! You have reached the finish line! Distance: ${Math.round(distance)}m`);
            
            // Optional: Play victory sound
            const audio = new Audio("sounds/victory.mp3");
            audio.volume = muted.value ? 0 : 0.7;
            audio.play();
        }
    }
}

function shareRaceResult({position, time}) {
    // Copy race result text to clipboard
    navigator.clipboard.writeText(
`I finished ${position} in the race from ${race.value.start} to ${race.value.end} with a time of ${time}!  

Can you beat my time? Join the fun at https://geo-racers.com`
);
}

function handleExchange({ from, to, rate }) {
    console.log({ from, to, rate })
    const fromEntry = money.value.find(m => m.code === from.currency);
    const toEntry   = money.value.find(m => m.code === to.currency);
    if (fromEntry) fromEntry.value = roundMoney(fromEntry.value - Number(from.amount));
    if (toEntry) {
        toEntry.value = roundMoney(toEntry.value + Number(to.amount));
    } else {
        money.value.push({ code: to.currency, name: to.name, symbol: to.symbol, value: roundMoney(to.amount) });
    }

    const audio = new Audio("sounds/kerching.mp3");
    audio.volume = muted.value ? 0 : 0.5;
    audio.play();

    notify({
        title: `Exchange Successful`,
        text: `You have successfully exchanged <strong>${from.symbol}${from.amount}</strong> to <strong>${to.symbol}${to.amount.toFixed(2)}</strong> at a rate of <strong>${rate}</strong>.`,
        type: "success",
        duration: 10000
    });
}
</script>

<!-- Optionally add CSS for markers and the confirm button -->
<style>
.transport-marker {
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 50px;
    height: 50px;
}
/*.station-marker {
  background: #00f;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #fff;
}*/
.confirm-route-button {
    cursor: pointer;
}
/* Styles for the person marker options overlay */
.person-options {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none; /* Allow clicks to fall through except on the buttons */
}
.person-option-button {
    position: absolute;
    pointer-events: auto;
    padding: 6px 12px;
    background-color: #333;
    color: #fff;
    border: none;
    border-radius: 4px;
    opacity: 0;
    animation: popIn 0.3s forwards;
}
@keyframes popIn {
    from {
        transform: translate(-50%, -50%) scale(0);
        opacity: 0;
    }
    to {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translate(-50%, 10px);
    }
    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}

.station-tooltip {
    position: fixed;
    padding: 10px;
    background: #ffffff;
    color: #000000;
    border: 1px solid #888888;
    border-radius: 8px;
    pointer-events: auto;
    z-index: 49;
    white-space: nowrap;
}
</style>
